import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../providers/ThemeProvider';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { SearchIcon, MapPinIcon, StarIcon, HeartIcon, ChevronDownIcon, MusicIcon, SlidersHorizontalIcon, XIcon, CheckIcon } from 'lucide-react';
// Teacher type matching the UI shape
interface Teacher {
  id: string;
  name: string;
  role: string;
  location: string;
  rating: number;
  reviews: number;
  bio: string;
  imageUrl: string;
  instruments: string[];
}
const INSTRUMENT_CHIPS = ['All Instruments', 'Piano', 'Guitar', 'Voice', 'Violin', 'Drums', 'Bass', 'Flute'];
const MORE_INSTRUMENTS = ['Cello', 'Saxophone', 'Trumpet', 'Clarinet', 'Harp', 'Ukulele', 'Trombone', 'Oboe'];
const ALL_INSTRUMENTS = [...INSTRUMENT_CHIPS.filter(i => i !== 'All Instruments'), ...MORE_INSTRUMENTS];
const SORT_OPTIONS = [{
  value: 'relevant',
  label: 'Most Relevant'
}, {
  value: 'rating',
  label: 'Highest Rating'
}, {
  value: 'name',
  label: 'Name (A–Z)'
}] as const;
type SortOption = (typeof SORT_OPTIONS)[number]['value'];
export const FindTeachers = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeInstrument, setActiveInstrument] = useState('All Instruments');
  const [sortBy, setSortBy] = useState<SortOption>('relevant');
  const [locationFilter, setLocationFilter] = useState('');
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  // Dropdown open states
  const [moreOpen, setMoreOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Filter panel local state (applied on "Apply")
  const [filterInstrument, setFilterInstrument] = useState('All Instruments');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterInstrumentDropdownOpen, setFilterInstrumentDropdownOpen] = useState(false);
  // Refs for click-outside
  const moreRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const filterInstrumentRef = useRef<HTMLDivElement>(null);
  // Real-time listener for public teacher profiles
  useEffect(() => {
    setIsLoadingTeachers(true);
    const publicProfilesRef = collection(db, 'publicTeacherProfiles');
    const publicQuery = query(publicProfilesRef, where('isPublic', '==', true));
    const unsubscribe = onSnapshot(publicQuery, snapshot => {
      const fetchedTeachers: Teacher[] = snapshot.docs.map(doc => {
        const data = doc.data();
        // Build display name
        const firstName = data.firstName || '';
        const lastName = data.lastName || '';
        const displayName = data.displayName || `${firstName} ${lastName}`.trim() || 'Teacher';
        // Build location string
        const city = data.city || '';
        const state = data.state || '';
        const location = data.location || [city, state].filter(Boolean).join(', ') || '';
        // Build instruments array
        const instruments: string[] = Array.isArray(data.instruments) ? data.instruments : [];
        // Build role from first instrument
        const role = instruments.length > 0 ? `${instruments[0]} Teacher` : 'Music Teacher';
        // Profile image with fallback
        const imageUrl = data.profileImageUrl || data.profilePictureUrl || data.profilePhotoUrl || '';
        return {
          id: doc.id,
          name: displayName,
          role,
          location,
          rating: typeof data.rating === 'number' ? data.rating : 0,
          reviews: typeof data.reviewCount === 'number' ? data.reviewCount : 0,
          bio: data.bio || data.tagline || '',
          imageUrl,
          instruments
        };
      });
      setTeachers(fetchedTeachers);
      setIsLoadingTeachers(false);
    }, error => {
      console.error('[FindTeachers] Error listening to public profiles:', error);
      setTeachers([]);
      setIsLoadingTeachers(false);
    });
    return () => unsubscribe();
  }, []);
  // Click-outside handler
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
      if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
        setFiltersOpen(false);
      }
      if (filterInstrumentRef.current && !filterInstrumentRef.current.contains(e.target as Node)) {
        setFilterInstrumentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Sync filter panel state when opening
  const handleOpenFilters = () => {
    setFilterInstrument(activeInstrument);
    setFilterLocation(locationFilter);
    setFiltersOpen(true);
  };
  const handleApplyFilters = () => {
    setActiveInstrument(filterInstrument);
    setLocationFilter(filterLocation);
    setFiltersOpen(false);
  };
  const handleClearFilters = () => {
    setFilterInstrument('All Instruments');
    setFilterLocation('');
  };
  // Client-side filtering + sorting
  const filteredTeachers = useMemo(() => {
    let results = teachers.filter(t => {
      // Instrument filter
      if (activeInstrument !== 'All Instruments' && !t.instruments.includes(activeInstrument)) {
        return false;
      }
      // Location filter
      if (locationFilter.trim()) {
        const loc = locationFilter.toLowerCase().trim();
        if (!t.location.toLowerCase().includes(loc)) {
          return false;
        }
      }
      // Search query filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const nameMatch = t.name.toLowerCase().includes(q);
        const locationMatch = t.location.toLowerCase().includes(q);
        const instrumentMatch = t.instruments.some(i => i.toLowerCase().includes(q));
        return nameMatch || locationMatch || instrumentMatch;
      }
      return true;
    });
    // Sorting
    if (sortBy === 'rating') {
      results = [...results].sort((a, b) => b.rating - a.rating || b.reviews - a.reviews);
    } else if (sortBy === 'name') {
      results = [...results].sort((a, b) => a.name.localeCompare(b.name));
    }
    // 'relevant' keeps default order
    return results;
  }, [searchQuery, activeInstrument, locationFilter, sortBy, teachers]);
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === sortBy)?.label || 'Most Relevant';
  return <div className={`min-h-screen ${isDark ? 'bg-[#0F1626]' : 'bg-[#F8FAFC]'} p-6 md:p-8 lg:p-10`} data-id="element-4216">
      <div className="max-w-[1400px] mx-auto" data-id="element-4217">
        {/* Header */}
        <div className="mb-8" data-id="element-4218">
          <h1 className={`text-3xl md:text-4xl font-bold mb-2 ${isDark ? 'text-white' : 'text-[#1A2A52]'}`} data-id="element-4219">
            Find Teachers
          </h1>
          <p className={`text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4220">
            Discover music teachers near you
          </p>
        </div>

        {/* Search Row */}
        <div className="flex flex-col md:flex-row gap-4 mb-6" data-id="element-4221">
          <div className="relative flex-1" data-id="element-4222">
            <SearchIcon className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-4223" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name, instrument, or location..." className={`w-full pl-11 pr-4 py-3.5 rounded-xl border outline-none transition-colors ${isDark ? 'bg-[#1A2A52] border-[#2A3A62] text-white placeholder-gray-500 focus:border-[#E8446D]' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#E8446D]'}`} data-id="element-4224" />
          </div>
          <div ref={filtersRef} className="relative" data-id="element-4225">
            <button onClick={handleOpenFilters} className={`flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border font-medium transition-colors whitespace-nowrap ${isDark ? 'border-[#E8446D]/30 text-[#E8446D] hover:bg-[#E8446D]/10' : 'border-[#E8446D]/30 text-[#E8446D] hover:bg-[#E8446D]/5 bg-white'} ${filtersOpen ? isDark ? 'bg-[#E8446D]/10' : 'bg-[#E8446D]/5' : ''}`} data-id="element-4226">
              <SlidersHorizontalIcon className="h-4 w-4" data-id="element-4227" />
              Filters
              {(locationFilter.trim() || activeInstrument !== 'All Instruments') && <span className="ml-1 w-2 h-2 rounded-full bg-[#E8446D]" data-id="element-4228" />}
            </button>

            {/* Filters Panel Dropdown */}
            {filtersOpen && <div className={`absolute right-0 top-full mt-2 w-80 rounded-xl border shadow-xl z-50 ${isDark ? 'bg-[#1A2A52] border-[#2A3A62]' : 'bg-white border-gray-200'}`} data-id="element-4229">
                <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? 'border-[#2A3A62]' : 'border-gray-100'}`} data-id="element-4230">
                  <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-[#1A2A52]'}`} data-id="element-4231">
                    Filters
                  </h3>
                  <button onClick={() => setFiltersOpen(false)} className={`p-1 rounded-full transition-colors ${isDark ? 'hover:bg-[#2A3A62] text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} data-id="element-4232">
                    <XIcon className="h-4 w-4" data-id="element-4233" />
                  </button>
                </div>
                <div className="px-5 py-4 space-y-5" data-id="element-4234">
                  {/* Instrument Filter */}
                  <div data-id="element-4235">
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-4236">
                      Instrument
                    </label>
                    <div ref={filterInstrumentRef} className="relative" data-id="element-4237">
                      <button onClick={() => setFilterInstrumentDropdownOpen(!filterInstrumentDropdownOpen)} className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border text-sm transition-colors ${isDark ? 'bg-[#0F1626] border-[#2A3A62] text-white hover:border-gray-500' : 'bg-white border-gray-200 text-gray-900 hover:border-gray-300'}`} data-id="element-4238">
                        <span data-id="element-4239">{filterInstrument}</span>
                        <ChevronDownIcon className={`h-4 w-4 transition-transform ${filterInstrumentDropdownOpen ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-4240" />
                      </button>
                      {filterInstrumentDropdownOpen && <div className={`absolute left-0 right-0 top-full mt-1 rounded-lg border shadow-lg z-50 max-h-48 overflow-y-auto ${isDark ? 'bg-[#0F1626] border-[#2A3A62]' : 'bg-white border-gray-200'}`} data-id="element-4241">
                          {['All Instruments', ...ALL_INSTRUMENTS].map(inst => <button key={inst} onClick={() => {
                      setFilterInstrument(inst);
                      setFilterInstrumentDropdownOpen(false);
                    }} className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between transition-colors ${filterInstrument === inst ? isDark ? 'bg-[#E8446D]/10 text-[#E8446D]' : 'bg-[#E8446D]/5 text-[#E8446D]' : isDark ? 'text-gray-300 hover:bg-[#1A2A52]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-4242">
                                <span data-id="element-4243">{inst}</span>
                                {filterInstrument === inst && <CheckIcon className="h-4 w-4" data-id="element-4244" />}
                              </button>)}
                        </div>}
                    </div>
                  </div>

                  {/* Location Filter */}
                  <div data-id="element-4245">
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-4246">
                      Location
                    </label>
                    <div className="relative" data-id="element-4247">
                      <MapPinIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-4248" />
                      <input type="text" value={filterLocation} onChange={e => setFilterLocation(e.target.value)} placeholder="e.g. San Francisco, CA" className={`w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm outline-none transition-colors ${isDark ? 'bg-[#0F1626] border-[#2A3A62] text-white placeholder-gray-500 focus:border-[#E8446D]' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#E8446D]'}`} data-id="element-4249" />
                    </div>
                  </div>
                </div>

                {/* Filter Actions */}
                <div className={`flex items-center justify-between px-5 py-4 border-t ${isDark ? 'border-[#2A3A62]' : 'border-gray-100'}`} data-id="element-4250">
                  <button onClick={handleClearFilters} className={`text-sm font-medium transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-4251">
                    Clear all
                  </button>
                  <button onClick={handleApplyFilters} className="px-5 py-2 rounded-lg text-sm font-medium bg-[#E8446D] text-white hover:bg-[#D63A5E] transition-colors" data-id="element-4252">
                    Apply Filters
                  </button>
                </div>
              </div>}
          </div>
        </div>

        {/* Instrument Chips */}
        <div className="flex items-center gap-3 pb-4 mb-4" data-id="element-4253">
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide" data-id="element-4254">
            {INSTRUMENT_CHIPS.map(chip => {
            const isActive = activeInstrument === chip;
            return <button key={chip} onClick={() => setActiveInstrument(chip)} className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors border ${isActive ? isDark ? 'bg-[#E8446D]/10 border-[#E8446D] text-[#E8446D]' : 'bg-[#E8446D]/5 border-[#E8446D] text-[#E8446D]' : isDark ? 'bg-transparent border-[#2A3A62] text-gray-400 hover:border-gray-500' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-4255">
                  {chip}
                </button>;
          })}
          </div>
          {/* More Dropdown — outside overflow container so it's never clipped */}
          <div ref={moreRef} className="relative flex-shrink-0" data-id="element-4256">
            <button onClick={() => setMoreOpen(!moreOpen)} className={`flex items-center gap-1 px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors border ${MORE_INSTRUMENTS.includes(activeInstrument) ? isDark ? 'bg-[#E8446D]/10 border-[#E8446D] text-[#E8446D]' : 'bg-[#E8446D]/5 border-[#E8446D] text-[#E8446D]' : isDark ? 'bg-transparent border-[#2A3A62] text-gray-400 hover:border-gray-500' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-4257">
              {MORE_INSTRUMENTS.includes(activeInstrument) ? activeInstrument : 'More'}{' '}
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${moreOpen ? 'rotate-180' : ''}`} data-id="element-4258" />
            </button>
            {moreOpen && <div className={`absolute left-0 top-full mt-2 w-48 rounded-xl border shadow-xl z-[60] py-1 ${isDark ? 'bg-[#1A2A52] border-[#2A3A62]' : 'bg-white border-gray-200'}`} data-id="element-4259">
                {MORE_INSTRUMENTS.map(inst => <button key={inst} onClick={() => {
              setActiveInstrument(inst);
              setMoreOpen(false);
            }} className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${activeInstrument === inst ? isDark ? 'bg-[#E8446D]/10 text-[#E8446D]' : 'bg-[#E8446D]/5 text-[#E8446D]' : isDark ? 'text-gray-300 hover:bg-[#0F1626]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-4260">
                    <span data-id="element-4261">{inst}</span>
                    {activeInstrument === inst && <CheckIcon className="h-4 w-4" data-id="element-4262" />}
                  </button>)}
              </div>}
          </div>
        </div>

        {/* Results Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-4263">
          <p className={`${isDark ? 'text-gray-300' : 'text-gray-800'}`} data-id="element-4264">
            <span className="font-semibold" data-id="element-4265">{filteredTeachers.length}</span>{' '}
            teachers found
          </p>
          <div className="flex items-center gap-3" data-id="element-4266">
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4267">
              Sort by:
            </span>
            <div ref={sortRef} className="relative" data-id="element-4268">
              <button onClick={() => setSortOpen(!sortOpen)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${isDark ? 'bg-[#1A2A52] border-[#2A3A62] text-white hover:border-gray-500' : 'bg-white border-gray-200 text-gray-900 hover:border-gray-300'}`} data-id="element-4269">
                {currentSortLabel}{' '}
                <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${sortOpen ? 'rotate-180' : ''}`} data-id="element-4270" />
              </button>
              {sortOpen && <div className={`absolute right-0 top-full mt-2 w-48 rounded-xl border shadow-xl z-50 py-1 ${isDark ? 'bg-[#1A2A52] border-[#2A3A62]' : 'bg-white border-gray-200'}`} data-id="element-4271">
                  {SORT_OPTIONS.map(option => <button key={option.value} onClick={() => {
                setSortBy(option.value);
                setSortOpen(false);
              }} className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${sortBy === option.value ? isDark ? 'bg-[#E8446D]/10 text-[#E8446D]' : 'bg-[#E8446D]/5 text-[#E8446D]' : isDark ? 'text-gray-300 hover:bg-[#0F1626]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-4272">
                      <span data-id="element-4273">{option.label}</span>
                      {sortBy === option.value && <CheckIcon className="h-4 w-4" data-id="element-4274" />}
                    </button>)}
                </div>}
            </div>
          </div>
        </div>

        {/* Teacher Cards Grid */}
        {isLoadingTeachers ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12" data-id="element-4275">
            {[1, 2, 3, 4].map(i => <div key={i} className={`flex flex-col rounded-2xl border overflow-hidden ${isDark ? 'bg-[#1A2A52] border-[#2A3A62]' : 'bg-white border-gray-200'}`} data-id="element-4276">
                <div className={`aspect-[4/3] w-full ${isDark ? 'bg-[#0F1626]' : 'bg-gray-200'} animate-pulse`} data-id="element-4277" />
                <div className="p-5 space-y-3" data-id="element-4278">
                  <div className={`h-5 w-2/3 rounded ${isDark ? 'bg-[#2A3A62]' : 'bg-gray-200'} animate-pulse`} data-id="element-4279" />
                  <div className={`h-4 w-1/2 rounded ${isDark ? 'bg-[#2A3A62]' : 'bg-gray-200'} animate-pulse`} data-id="element-4280" />
                  <div className={`h-4 w-3/4 rounded ${isDark ? 'bg-[#2A3A62]' : 'bg-gray-200'} animate-pulse`} data-id="element-4281" />
                  <div className={`h-10 w-full rounded-lg ${isDark ? 'bg-[#2A3A62]' : 'bg-gray-200'} animate-pulse`} data-id="element-4282" />
                </div>
              </div>)}
          </div> : filteredTeachers.length === 0 ? <div className={`text-center py-16 mb-12 rounded-2xl border ${isDark ? 'bg-[#1A2A52] border-[#2A3A62]' : 'bg-white border-gray-200'}`} data-id="element-4283">
            <MusicIcon className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-4284" />
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-4285">
              No teachers found
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-4286">
              Try adjusting your search or filters
            </p>
          </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12" data-id="element-4287">
            {filteredTeachers.map(teacher => <div key={teacher.id} className={`flex flex-col rounded-2xl border overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${isDark ? 'bg-[#1A2A52] border-[#2A3A62] hover:shadow-black/20' : 'bg-white border-gray-200 hover:shadow-gray-200/50'}`} data-id="element-4288">
                {/* Image Container */}
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-gray-200" data-id="element-4289">
                  {teacher.imageUrl ? <img src={teacher.imageUrl} alt={teacher.name} className="w-full h-full object-cover" onError={e => {
              e.currentTarget.style.display = 'none';
            }} data-id="element-4290" /> : <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-[#0F1626]' : 'bg-gray-100'}`} data-id="element-4291">
                      <MusicIcon className={`h-12 w-12 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-4292" />
                    </div>}
                  <button className="absolute top-3 right-3 p-2 rounded-full bg-white/90 hover:bg-white text-gray-400 hover:text-[#E8446D] transition-colors shadow-sm backdrop-blur-sm" data-id="element-4293">
                    <HeartIcon className="h-4 w-4" data-id="element-4294" />
                  </button>
                </div>

                {/* Card Content */}
                <div className="flex flex-col flex-1 p-5" data-id="element-4295">
                  <h3 className={`text-lg font-bold mb-1 ${isDark ? 'text-white' : 'text-[#1A2A52]'}`} data-id="element-4296">
                    {teacher.name}
                  </h3>
                  <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4297">
                    {teacher.role}
                  </p>

                  {teacher.location && <div className="flex items-center gap-1.5 mb-2" data-id="element-4298">
                      <MapPinIcon className={`h-4 w-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-4299" />
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-4300">
                        {teacher.location}
                      </span>
                    </div>}

                  {teacher.rating > 0 && <div className="flex items-center gap-1.5 mb-4" data-id="element-4301">
                      <StarIcon className="h-4 w-4 fill-[#E8446D] text-[#E8446D]" data-id="element-4302" />
                      <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-4303">
                        {teacher.rating.toFixed(1)}
                      </span>
                      <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-4304">
                        ({teacher.reviews} reviews)
                      </span>
                    </div>}

                  {teacher.bio && <p className={`text-sm leading-relaxed mb-6 line-clamp-2 flex-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4305">
                      {teacher.bio}
                    </p>}

                  <button onClick={() => navigate(`/teachers/${teacher.id}`)} className={`w-full py-2.5 rounded-lg text-sm font-medium border transition-colors mt-auto ${isDark ? 'border-[#E8446D]/50 text-[#E8446D] hover:bg-[#E8446D]/10' : 'border-[#E8446D]/30 text-[#E8446D] hover:bg-[#E8446D]/5'}`} data-id="element-4306">
                    View Profile
                  </button>
                </div>
              </div>)}
          </div>}

        {/* Bottom CTA Banner */}
        <div className={`flex flex-col md:flex-row items-center justify-between gap-6 p-6 md:p-8 rounded-2xl border ${isDark ? 'bg-[#1A2A52] border-[#2A3A62]' : 'bg-white border-gray-200'}`} data-id="element-4307">
          <div data-id="element-4308">
            <h3 className={`text-lg font-bold mb-1 ${isDark ? 'text-white' : 'text-[#1A2A52]'}`} data-id="element-4309">
              Can't find the right teacher?
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4310">
              Tell us what you're looking for and we'll help match you.
            </p>
          </div>
          <button onClick={() => setRequestModalOpen(true)} className={`px-6 py-3 rounded-xl text-sm font-medium border transition-colors whitespace-nowrap ${isDark ? 'border-[#E8446D]/50 text-[#E8446D] hover:bg-[#E8446D]/10' : 'border-[#E8446D]/30 text-[#E8446D] hover:bg-[#E8446D]/5'}`} data-id="element-4311">
            Request a Teacher
          </button>
        </div>
      </div>

      {/* Request a Teacher Modal */}
      {requestModalOpen && <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4312">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setRequestModalOpen(false)} data-id="element-4313" />
          <div className={`relative w-full max-w-md rounded-2xl border shadow-2xl p-8 text-center ${isDark ? 'bg-[#1A2A52] border-[#2A3A62]' : 'bg-white border-gray-200'}`} data-id="element-4314">
            <button onClick={() => setRequestModalOpen(false)} className={`absolute top-4 right-4 p-1 rounded-full transition-colors ${isDark ? 'hover:bg-[#2A3A62] text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} data-id="element-4315">
              <XIcon className="h-5 w-5" data-id="element-4316" />
            </button>
            <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1A2A52]'}`} data-id="element-4317">
              Request a Teacher
            </h3>
            <p className={`text-sm leading-relaxed mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4318">
              This feature is coming soon. We're working on helping you get
              matched with the perfect teacher.
            </p>
            <button onClick={() => setRequestModalOpen(false)} className="px-8 py-2.5 rounded-xl text-sm font-medium bg-[#E8446D] text-white hover:bg-[#D63A5E] transition-colors" data-id="element-4319">
              Close
            </button>
          </div>
        </div>}
    </div>;
};